import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { timestampFormatter } from '@retainerclub/common';
import PropTypes from 'prop-types';

const OPTION_CLASSES =
  'group relative flex rounded-lg border border-gray-300 bg-white p-4 has-disabled:border-gray-400 has-disabled:bg-gray-200 has-disabled:opacity-25 has-checked:outline-2 has-checked:outline-rcprimary-600 has-focus-visible:outline-3 has-checked:-outline-offset-2 has-focus-visible:-outline-offset-1';

function EffectiveDateOption({
  label,
  description,
  ariaLabel,
  checked,
  onSelect,
}) {
  return (
    <label aria-label={ariaLabel} className={OPTION_CLASSES}>
      {/*
        Controlled rather than defaultChecked: when only one option is available the parent sets
        `now` from an effect after mount, and an uncontrolled input keeps its mount-time checked
        state. That rendered the single available option with no selection indicator while the
        form submitted the opposite value.

        opacity-0 rather than `hidden`, because display:none takes the radio out of the tab order
        entirely. The label's has-focus-visible ring could never fire against a display:none input.
      */}
      <input
        checked={checked}
        onChange={onSelect}
        name="now"
        type="radio"
        className="absolute inset-0 appearance-none opacity-0 focus:outline-none"
      />
      <div className="flex-1">
        <span className="block font-medium text-gray-900 text-sm">{label}</span>
        <span className="mt-1 block text-gray-500 text-sm">{description}</span>
      </div>
      <CheckCircleIcon
        aria-hidden="true"
        className="invisible size-5 text-rcprimary-600 group-has-checked:visible"
      />
    </label>
  );
}

EffectiveDateOption.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function EffectiveDateChoice({ subscription, tier, now, setNow, options }) {
  const {
    canChooseNow,
    canChooseOnRenewal,
    renewalPastDue,
    hasNoEffectiveDateOption,
  } = options;

  return (
    <>
      <div className="rounded-lg border border-rcgray-200 bg-rcgray-100 p-4">
        <h4 className="mb-2 font-medium text-gray-900">
          When would you like this to take effect?
        </h4>

        {canChooseNow && (
          <>
            <p>Now</p>
            <ul className="list-disc pl-8 text-sm">
              <li>Start {tier.product.name} today</li>
              <li>New retainers ship ASAP</li>
              <li>Charged today</li>
            </ul>
          </>
        )}

        {canChooseOnRenewal && (
          <>
            <p>
              On renewal ({timestampFormatter(subscription.renewalAt, false)})
            </p>
            <ul className="list-disc pl-8 text-sm">
              <li>Start {tier.product.name} on your renewal date</li>
              <li>Keep current shipment schedule</li>
              <li>No charge today</li>
            </ul>
          </>
        )}

        {renewalPastDue && canChooseNow && (
          <p className="mt-2 text-gray-600 text-sm">
            This subscription&apos;s renewal is past due, so the change takes
            effect today rather than on a future renewal date.
          </p>
        )}

        {hasNoEffectiveDateOption && (
          <p className="text-gray-700 text-sm">
            We can&apos;t change this plan from here right now. Please reach out
            to Customer Service and we&apos;ll get this sorted out for you.
          </p>
        )}
      </div>

      {!hasNoEffectiveDateOption && (
        <div
          className={`mt-2 grid gap-4 ${
            canChooseNow && canChooseOnRenewal
              ? 'grid-cols-2 sm:grid-cols-2'
              : 'grid-cols-1'
          }`}
        >
          {canChooseNow && (
            <EffectiveDateOption
              ariaLabel="now"
              label="Now"
              description="Your new subscription will start today."
              checked={now}
              onSelect={() => setNow(true)}
            />
          )}
          {canChooseOnRenewal && (
            <EffectiveDateOption
              ariaLabel="on renewal"
              label="On renewal"
              description="Your new subscription will start on your next renewal date."
              checked={!now}
              onSelect={() => setNow(false)}
            />
          )}
        </div>
      )}
    </>
  );
}

EffectiveDateChoice.propTypes = {
  subscription: PropTypes.object.isRequired,
  tier: PropTypes.object.isRequired,
  now: PropTypes.bool.isRequired,
  setNow: PropTypes.func.isRequired,
  options: PropTypes.object.isRequired,
};

export default EffectiveDateChoice;
